import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Owns the human-confirmed setup checklist (geofence / property lines / takeoff)
// for a jobsite. Seeds from jobsite.setupChecklist (with the legacy
// geofenceConfirmed fallback) and confirms via PUT /jobsite/:id/setup-checklist.
// "Areas mapped" is NOT here — it stays derived from areas.length upstream.
const CHECKLIST_ITEMS = ['geofenceSet', 'propertyLinesVerified', 'takeoffComplete'];

const seedItem = (jobsite, key) => {
  const item = jobsite?.setupChecklist?.[key] || {};
  let confirmed = item.confirmed === true;
  if (key === 'geofenceSet' && !confirmed && jobsite?.geofenceConfirmed === true) {
    confirmed = true;
  }
  return {
    confirmed,
    confirmedAt: item.confirmedAt || null,
    confirmedBy: item.confirmedBy || null,
    notes: key === 'takeoffComplete' ? (item.notes || null) : undefined,
  };
};

export default function useSetupChecklist(url, jobsite) {
  const jobsiteId = jobsite?._id || null;
  const [checklist, setChecklist] = useState(() => ({
    geofenceSet: seedItem(jobsite, 'geofenceSet'),
    propertyLinesVerified: seedItem(jobsite, 'propertyLinesVerified'),
    takeoffComplete: seedItem(jobsite, 'takeoffComplete'),
  }));
  const [saving, setSaving] = useState(null); // which item is mid-save

  // Re-seed when the jobsite (re)loads.
  useEffect(() => {
    setChecklist({
      geofenceSet: seedItem(jobsite, 'geofenceSet'),
      propertyLinesVerified: seedItem(jobsite, 'propertyLinesVerified'),
      takeoffComplete: seedItem(jobsite, 'takeoffComplete'),
    });
  }, [jobsite]);

  const confirmItem = useCallback(async (item, confirmed, notes) => {
    if (!url || !jobsiteId || !CHECKLIST_ITEMS.includes(item)) return;
    setSaving(item);
    // Optimistic — reflect immediately; the server stamps confirmedAt/By. Snapshot
    // the prior item so we can roll back if the write fails.
    let prevItem;
    setChecklist(prev => {
      prevItem = prev[item];
      return {
        ...prev,
        [item]: {
          ...prev[item],
          confirmed,
          ...(notes !== undefined ? { notes } : {}),
        },
      };
    });
    try {
      const body = { item, confirmed };
      if (item === 'takeoffComplete' && notes !== undefined) body.notes = notes;
      const resp = await axios.put(`${url}/api/customer/jobsite/${jobsiteId}/setup-checklist`, body);
      if (resp.data?.setupChecklist) {
        const sc = resp.data.setupChecklist;
        setChecklist({
          geofenceSet: seedItem({ setupChecklist: sc }, 'geofenceSet'),
          propertyLinesVerified: seedItem({ setupChecklist: sc }, 'propertyLinesVerified'),
          takeoffComplete: seedItem({ setupChecklist: sc }, 'takeoffComplete'),
        });
      }
    } catch (err) {
      console.error('confirmItem error:', err);
      // Roll back the optimistic change so the UI doesn't show a false confirm.
      setChecklist(prev => ({ ...prev, [item]: prevItem }));
    } finally {
      setSaving(null);
    }
  }, [url, jobsiteId]);

  return { checklist, confirmItem, saving };
}
